import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationSetAccessGrantsIsOfficeModeEnabledArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED } from 'graphql/queries';
import { useEffect } from 'react';
import {
  setIsOfficeModeEnabledAction,
  updateAccessGrantsArrayWithIsOfficeModeEnabled,
  updateIsOfficeModeEnabledAction
} from 'state/actions/isOfficeModeEnabled';
import { visitorGroupsWithIsOfficeModeEnabledVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>
> = GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED;
const UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setAccessGrantsIsOfficeModeEnabled'>,
  MutationSetAccessGrantsIsOfficeModeEnabledArgs
> = UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED;

const useIsOfficeModeEnabled = ({ handleFetchError }: BaseHookProps) => {
  const visitorGroupsWithIsOfficeModeEnabled = useReactiveVar(visitorGroupsWithIsOfficeModeEnabledVar);

  const [getVisitorGroupsWithIsOfficeModeEnabled, { loading }] = useLazyQuery(
    GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED_TYPED,
    { fetchPolicy: 'network-only' }
  );

  const checkIsOfficeModeEnabledForVisitorGroup = (id: string) => {
    const visitorGroupById = visitorGroupsWithIsOfficeModeEnabled.find((group) => group.id === id);
    return visitorGroupById && visitorGroupById.accessGrants.some((grant) => grant.isOfficeModeEnabled);
  };

  const getAccessGrantWithIsOfficeModeEnabledForGroup = (group: VisitorGroup, isOfficeModeEnabledForGroup: boolean) =>
    group.accessGrants.find((grant) => grant.isOfficeModeEnabled === isOfficeModeEnabledForGroup);

  const [
    updateAccessGrantIsOfficeModeEnabledMutation,
    { loading: updateAccessGrantIsOfficeModeEnabledMutationLoading }
  ] = useMutation(UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED_TYPED, {
    onCompleted: (data) => {
      updateIsOfficeModeEnabledAction(data.setAccessGrantsIsOfficeModeEnabled.id);
    },
    onError: () => {
      handleFetchError('Error while updating Access Grant');
    }
  });

  useEffect(() => {
    const fetchVisitorGroupsWithIsOfficeModeEnabled = async () => {
      try {
        const { data } = await getVisitorGroupsWithIsOfficeModeEnabled();
        if (data) {
          setIsOfficeModeEnabledAction(data.getVisitorGroupsForTenant);
        }
      } catch {
        handleFetchError('Error while fetching groups with officeMode');
      }
    };
    void fetchVisitorGroupsWithIsOfficeModeEnabled();
  }, [getVisitorGroupsWithIsOfficeModeEnabled]);

  const updateAccessGrantIsOfficeModeEnabled = async (groupId: string) => {
    const isOfficeModeEnabledForGroup = checkIsOfficeModeEnabledForVisitorGroup(groupId);
    const groupWithAccessGrant = visitorGroupsWithIsOfficeModeEnabled.find((group) => group.id === groupId);
    if (groupWithAccessGrant && isOfficeModeEnabledForGroup !== undefined) {
      const accessGrant = getAccessGrantWithIsOfficeModeEnabledForGroup(
        groupWithAccessGrant,
        isOfficeModeEnabledForGroup
      );
      const { data } = await ignoreHandledMutationError(
        updateAccessGrantIsOfficeModeEnabledMutation({
          variables: {
            accessGrantId: accessGrant!.id,
            isOfficeModeEnabled: !isOfficeModeEnabledForGroup
          },
          // Here Apollo doesn't update cache automatically, we have to do it manually
          update(cache, { data: updatedAccessGrantId }) {
            if (updatedAccessGrantId?.setAccessGrantsIsOfficeModeEnabled) {
              const updatedVisitorGroups = visitorGroupsWithIsOfficeModeEnabled.map((group) => ({
                ...group,
                accessGrants: updateAccessGrantsArrayWithIsOfficeModeEnabled(
                  updatedAccessGrantId.setAccessGrantsIsOfficeModeEnabled.id,
                  group.accessGrants
                )
              }));

              cache.writeQuery({
                query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED,
                data: {
                  getVisitorGroupsForTenant: updatedVisitorGroups
                }
              });
            }
          }
        })
      );

      return data;
    }
    return null;
  };

  return {
    visitorGroupsWithIsOfficeModeEnabled,
    checkIsOfficeModeEnabledForVisitorGroup,
    updateAccessGrantIsOfficeModeEnabled,
    updateAccessGrantIsOfficeModeEnabledMutationLoading,
    loading
  };
};

export default useIsOfficeModeEnabled;
